import { cleanExtraParams, GTM, GTMEvents } from './analytics';

export type CheckoutErrorScope = 'entry' | 'validation' | 'order' | 'payment';
export type CheckoutErrorSurfaced = 'message' | 'redirect' | 'silent';

type CheckoutErrorLike = {
  data?: {
    data?: {
      errorCode?: string;
      code?: string;
      errors?: Record<string, unknown> & { shipping?: { code?: string } };
      details?: { responseBody?: { data?: { message?: string } } };
    };
  };
};

// A provider-echoed message only counts as a code if it looks like a real code (short,
// SCREAMING_SNAKE-ish token) rather than free text - GA4 truncates param values at 100 chars
// (silently corrupting long messages), an unbounded-cardinality dimension gets bucketed into
// `(other)` by GA4 (destroying it for the well-formed codes too), and some providers (e.g.
// Mpesa, MSISDN-based) can echo request data that includes a phone number back in this field.
// Leading letter required - otherwise a bare MSISDN (all digits) passes as "code-like", which is
// exactly the case this guard exists to block. Case-insensitive since providers aren't guaranteed
// to send SCREAMING_SNAKE (e.g. a lowercase insufficient_funds shouldn't collapse to the sentinel).
const PROVIDER_CODE_LIKE = /^[A-Z][A-Z0-9_]{0,39}$/i;
const UNMAPPED_PROVIDER_ERROR = 'unmapped_provider_error';

/**
 * Best-effort extraction of a single error code out of the four shapes a checkout error can
 * arrive in, so callers don't have to repeat the `error?.data?.data?...` chain themselves.
 * Not exhaustive by design - it's meant to answer "what's the code", not to replace the
 * existing branches that decide the buyer-facing message/field.
 *
 * Naming convention note: this dimension mixes backend SCREAMING_SNAKE codes (`errorCode`/`code`),
 * camelCase field names taken from `Object.keys(errors)[0]` (e.g. `paymentPhone`), and
 * client-minted snake_case sentinels from call sites (e.g. `access_forbidden`, `payment_failed`).
 * Grouping these in GA4/BigQuery needs an external lookup table today; the mobile stream unions
 * into the same field and isn't normalized to match either. Left as-is for now (a rename would
 * need to land in lockstep with the mobile PR) - tracked as follow-up, not silently dropped.
 *
 * Also note `Object.keys(data.errors)[0]` depends on the server's key serialization order, so
 * with two failing fields the reported code is order-dependent. Acceptable for best-effort, but
 * worth knowing when reading the data.
 */
export function extractCheckoutErrorCode(error: unknown): string | undefined {
  const data = (error as CheckoutErrorLike)?.data?.data;
  const providerMessage = data?.details?.responseBody?.data?.message;

  return (
    data?.errorCode ||
    data?.code ||
    data?.errors?.shipping?.code ||
    (data?.errors ? Object.keys(data.errors)[0] : undefined) ||
    (providerMessage && PROVIDER_CODE_LIKE.test(providerMessage) ? providerMessage : undefined) ||
    (providerMessage ? UNMAPPED_PROVIDER_ERROR : undefined)
  );
}

export function trackCheckoutError(params: {
  scope: CheckoutErrorScope;
  code?: string;
  surfaced: CheckoutErrorSurfaced;
  product_slug?: string;
  shop_slug?: string;
  checkout_visit_id?: string;
  gateway?: string;
}) {
  GTM.logEvent(GTMEvents.CHECKOUT_ERROR, cleanExtraParams(params));
}
